import last from 'lodash/last';
import AdministrationEvent from '../vcom/events/AdministrationEvent';
import HttpService from '../common/http/HttpService';
import Endpoint from './const/Endpoint';
import VcomProvider from '../VcomProvider';
import Base from '../vcom/Base';
import EventService from '../common/event/EventService';
import EventLoggingService from '../eventLogging/EventLoggingService';
import FrontendEvents from '../eventLogging/const/FrontendEvents';
import RouteNames from './const/RouteNames';

async function loadMenu(systemId) {
  const response = await HttpService.get(
    Endpoint.GET_MENU,
    {
      params: {
        renderDarkSspTheme: VcomProvider.Layout.getTheme() === 'darkify',
        systemId,
      },
    },
  );
  return response.data;
}

async function loadSystemImage(systemId) {
  const response = await HttpService.get(
    Endpoint.GET_SYSTEM_IMAGE,
    {
      params: {
        systemId,
      },
    },
  );
  return response.data;
}

async function loadSystemName(systemId) {
  const response = await HttpService.get(Endpoint.GET_SYSTEM_NAME, { params: { systemId } });
  return response.data;
}

function extractParentChildRoute(fullPath) {
  const splitted = fullPath.split('/');

  if (VcomProvider.adminSystemId) {
    const topMenuEntry = splitted[3];
    const leftMenuEntry = splitted[4];
    return [topMenuEntry, leftMenuEntry];
  }

  const topMenuEntry = splitted[2];
  const leftMenuEntry = splitted[3];
  return [topMenuEntry, leftMenuEntry];
}

function extractParentChildDataloggerRoute(fullPath) {
  const splitted = fullPath.split('/');
  const dataLoggerChild = splitted.slice(5, splitted.length + 1).join('/');
  const dataLoggerParent = splitted[3];
  return [dataLoggerParent, dataLoggerChild];
}

function extractRoutes(fullPath) {
  if (fullPath.includes(RouteNames.DIGITAL_SIGNALS)) {
    const dataloggerRoute = extractParentChildDataloggerRoute(fullPath);
    return [dataloggerRoute[0], `${RouteNames.DIGITAL_SIGNALS}_${dataloggerRoute[1]}`];
  }

  if (fullPath.includes(RouteNames.BATTERY_INVERTER_ASSIGNMENTS)) {
    const dataloggerRoute = extractParentChildDataloggerRoute(fullPath);
    return [dataloggerRoute[0], `${RouteNames.BATTERY_INVERTER_ASSIGNMENTS}_${dataloggerRoute[1]}`];
  }

  if (fullPath.includes(RouteNames.INVERTER_STRING_MONITORING)) {
    const dataloggerRoute = extractParentChildDataloggerRoute(fullPath);
    return [dataloggerRoute[0], `${RouteNames.INVERTER_STRING_MONITORING}_${dataloggerRoute[1]}`];
  }

  if (fullPath.includes('/dataSource/datalogger/')) {
    return extractParentChildDataloggerRoute(fullPath);
  }

  if (fullPath.includes('editor')) {
    return ['calculated', RouteNames.PERFORMANCE_RATIO_EDITOR];
  }

  return extractParentChildRoute(fullPath);
}

function extractSystemId(fullPath) {
  const splitted = fullPath.split('/');
  const systemId = Number(splitted[2]);

  if (Number.isNaN(systemId)) {
    return null;
  }

  return systemId;
}

function loadSspUrl(fullPath, menu) {
  const [parent, child] = extractParentChildRoute(fullPath);

  const pages = menu[parent];
  const sspPage = pages.find((page) => page.name === child);
  return sspPage?.url;
}

function parseDataloggerUrl(menu, path, name) {
  if (menu.length === 0) {
    return undefined;
  }

  const pathSplitted = path.split('/');
  const index = pathSplitted[0];
  const newPath = pathSplitted.slice(1, pathSplitted.length + 1).join('/');

  if (menu[index]?.children.length > 0) {
    return parseDataloggerUrl(menu[index]?.children ?? [], newPath, name);
  }

  if (menu[index]?.sName !== decodeURIComponent(name)) {
    return undefined;
  }

  return menu[index].sUrl;
}

function getDataloggerUrl(uniqueUrl, menu) {
  const uniqueUrlSplitted = uniqueUrl.split('/');
  const uniqueName = last(uniqueUrlSplitted);
  return parseDataloggerUrl(menu.dataSource, uniqueUrl, uniqueName);
}

async function getSystemName(systemId) {
  if (systemId === VcomProvider.systemId && VcomProvider.systemName) {
    return VcomProvider.systemName;
  }
  const systems = await Base.getSystems();
  return systems[systemId]?.label ?? loadSystemName(systemId);
}

async function hasValidLicense(systemId) {
  try {
    if (VcomProvider.isSuperAdmin || VcomProvider.isPartnerAdmin) {
      return true;
    }
    const response = await HttpService.get(
      Endpoint.HAS_VALID_LICENSE,
      {
        params: {
          systemId,
        },
      },
    );
    return response.data;
  } catch (error) {
    return false;
  }
}

function bindListenerSsp() {
  document.getElementById('ssp-iframe')
    .contentDocument
    .querySelectorAll('.new-page-system')
    .forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        const dataSourceRoutes = [
          RouteNames.INVERTER_STRING_MONITORING,
          RouteNames.DIGITAL_SIGNALS,
          RouteNames.BATTERY_INVERTER_ASSIGNMENTS,
        ];
        if (dataSourceRoutes.includes(event.currentTarget.dataset.newPage)) {
          window.location.hash = `#/administration/${VcomProvider.adminSystemId}/dataSource/${event.currentTarget.dataset.newPage}/${event.currentTarget.dataset.dataloggerId}`;
        } else {
          window.location.hash = `#/administration/${VcomProvider.adminSystemId}/${event.currentTarget.dataset.newPage}`;
        }
        EventService.dispatch(AdministrationEvent.RELOAD_PAGE);
      });
    });
}

function logLeftMenuClick(page) {
  if (VcomProvider.adminSystemId) {
    EventLoggingService.logFrontendEvent(FrontendEvents.SYSTEM_ADMINISTRATION_LINK_CLICK(page));
  } else {
    EventLoggingService.logFrontendEvent(FrontendEvents.PORTFOLIO_ADMINISTRATION_LINK_CLICK(page));
  }
}

const AdministrationMenuService = {
  bindListenerSsp,
  extractSystemId,
  extractRoutes,
  getSystemName,
  getDataloggerUrl,
  hasValidLicense,
  loadMenu,
  loadSspUrl,
  loadSystemImage,
  logLeftMenuClick,
};

export default AdministrationMenuService;
